// Chiffrement applicatif E2E (Vague 3 « WS-O ») du payload vidéo entre
// l'opérateur (ce navigateur) et l'agent C#, AU-DESSUS du WebSocket. Le relais
// — et un proxy MITM-TLS en firewall strict — ne voit qu'un payload opaque.
//
// Suite : ECDH P-256 (secp256r1) → HKDF-SHA256 → AES-256-GCM. Interop exacte
// avec agent-windows-csharp/RemoteWS.Agent/Crypto/E2eCrypto.cs :
//   - courbe NIST P-256 (WebCrypto la supporte partout, contrairement à X25519) ;
//   - clés publiques échangées au format SPKI base64 (exportKey/importKey "spki"
//     côté WebCrypto ⇔ ExportSubjectPublicKeyInfo/ImportSubjectPublicKeyInfo
//     côté .NET) ;
//   - HKDF info ASCII "RemoteWS-E2E-v1/video", sel 32 octets fourni par l'agent ;
//   - AES-256-GCM, tag 128 bits, nonce 96 bits = compteur 64 bits LITTLE-ENDIAN
//     (8 octets) + 4 octets à zéro, PRÉFIXÉ au ciphertext.
//
// Toggle OFF par défaut : tant que `establish()` n'a pas abouti, ce module n'est
// pas sollicité et le flux vidéo reste STRICTEMENT le clair actuel.

import { base64ToBytes, bytesToBase64 } from "./credential-encryption";

/** Identifiant de suite annoncé dans le handshake (doit matcher le C#). */
export const E2E_SUITE = "ECDH-P256+HKDF-SHA256+A256GCM";

const NONCE_BYTES = 12;
const TAG_BYTES = 16;
/** Octets ajoutés à chaque frame chiffrée (nonce + tag GCM). */
export const E2E_FRAME_OVERHEAD = NONCE_BYTES + TAG_BYTES; // 28

const HKDF_INFO = new TextEncoder().encode("RemoteWS-E2E-v1/video");
const ECDH_PARAMS: EcKeyImportParams = { name: "ECDH", namedCurve: "P-256" };

function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error("WebCrypto indisponible (E2E impossible).");
  return subtle;
}

function copyToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

/**
 * Session E2E établie : porte la clé AES-GCM dérivée et le compteur de nonce
 * d'émission (chemin opérateur→agent, ex. input chiffré futur). Le déchiffrement
 * des frames vidéo lit le nonce DANS le paquet — aucun compteur partagé requis.
 */
export class E2eSession {
  private sendCounter = 0n;

  private constructor(private readonly key: CryptoKey) {}

  /**
   * Handshake côté opérateur :
   *   1. génère une paire ECDH P-256 éphémère, exporte la clé publique (SPKI b64)
   *      → à envoyer dans `e2e:hello` ;
   *   2. (plus tard) `complete()` importe la clé publique de l'agent + le sel
   *      reçus dans `e2e:key`, dérive la clé AES-GCM.
   *
   * Retourne `{ publicKeyB64, complete }`. `complete()` peut renvoyer `null` si
   * l'entrée distante est invalide — l'appelant retombe alors sur le clair.
   */
  static async begin(): Promise<{
    publicKeyB64: string;
    complete: (agentPublicSpkiB64: string, saltB64: string) => Promise<E2eSession | null>;
  }> {
    const subtle = getSubtle();
    const pair = await subtle.generateKey(
      { name: "ECDH", namedCurve: "P-256" },
      false, // privée non-extractible
      ["deriveBits"],
    );
    const spki = await subtle.exportKey("spki", pair.publicKey);
    const publicKeyB64 = bytesToBase64(new Uint8Array(spki));

    const complete = async (agentPublicSpkiB64: string, saltB64: string): Promise<E2eSession | null> => {
      try {
        const agentPub = await subtle.importKey(
          "spki",
          copyToArrayBuffer(base64ToBytes(agentPublicSpkiB64)),
          ECDH_PARAMS,
          false,
          [],
        );
        // Secret ECDH brut (coordonnée x, 256 bits = 32 octets) → IKM de HKDF.
        const sharedBits = await subtle.deriveBits({ name: "ECDH", public: agentPub }, pair.privateKey, 256);
        const hkdfKey = await subtle.importKey("raw", sharedBits, "HKDF", false, ["deriveKey"]);
        const salt = copyToArrayBuffer(base64ToBytes(saltB64));
        const aesKey = await subtle.deriveKey(
          { name: "HKDF", hash: "SHA-256", salt, info: HKDF_INFO },
          hkdfKey,
          { name: "AES-GCM", length: 256 },
          false,
          ["encrypt", "decrypt"],
        );
        return new E2eSession(aesKey);
      } catch (err) {
        console.warn("[e2e] complete() échoué — fallback clair :", err);
        return null;
      }
    };

    return { publicKeyB64, complete };
  }

  /**
   * Déchiffre un payload de frame vidéo `[nonce 12][ciphertext][tag 16]` (le
   * découpage AES-GCM de WebCrypto attend ciphertext||tag concaténés). Renvoie
   * `null` si le paquet est trop court ou si l'authentification GCM échoue
   * (frame altérée) — l'appelant jette alors la frame sans crash.
   */
  async decryptFrame(packet: Uint8Array): Promise<Uint8Array | null> {
    if (packet.byteLength < NONCE_BYTES + TAG_BYTES) return null;
    const nonce = packet.subarray(0, NONCE_BYTES);
    const cipherAndTag = packet.subarray(NONCE_BYTES); // ciphertext || tag (WebCrypto)
    try {
      const plain = await getSubtle().decrypt(
        { name: "AES-GCM", iv: copyToArrayBuffer(nonce), tagLength: TAG_BYTES * 8 },
        this.key,
        copyToArrayBuffer(cipherAndTag),
      );
      return new Uint8Array(plain);
    } catch {
      return null;
    }
  }

  /**
   * Chiffre un payload opérateur→agent (chemin de contrôle chiffré, ex. input).
   * Renvoie `[nonce 12][ciphertext][tag 16]`. Le nonce est un compteur monotone
   * little-endian 96 bits — interop exacte avec le C# (`WriteNonce`).
   */
  async encryptFrame(plaintext: Uint8Array): Promise<Uint8Array> {
    const nonce = new Uint8Array(NONCE_BYTES);
    const counter = this.sendCounter++;
    // 8 octets little-endian (poids faible), 4 octets de poids fort à zéro.
    new DataView(nonce.buffer).setBigUint64(0, counter, true);
    const ct = await getSubtle().encrypt(
      { name: "AES-GCM", iv: copyToArrayBuffer(nonce), tagLength: TAG_BYTES * 8 },
      this.key,
      copyToArrayBuffer(plaintext),
    );
    const ctBytes = new Uint8Array(ct);
    const out = new Uint8Array(NONCE_BYTES + ctBytes.byteLength);
    out.set(nonce, 0);
    out.set(ctBytes, NONCE_BYTES);
    return out;
  }
}

/** `true` si WebCrypto + ECDH P-256 sont disponibles (sinon : pas de négo E2E). */
export function isE2eSupported(): boolean {
  return typeof globalThis.crypto?.subtle?.deriveBits === "function";
}

/**
 * `true` si le toggle E2E est ON (posé sur `window` au boot par bootstrapConfig
 * depuis `/config.json.e2eEncryption`). OFF par défaut. À combiner avec
 * `isE2eSupported()` avant de tenter un handshake.
 */
export function e2eEncryptionFlag(): boolean {
  return (window as unknown as { __remotews_e2e_encryption__?: boolean }).__remotews_e2e_encryption__ === true;
}
